/**
 * Property Renderer Factory
 * Clean separation of property rendering logic
 */

import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { 
  AlignLeft, 
  AlignCenter, 
  AlignRight, 
  AlignJustify,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Upload,
  X,
  Lock,
  Unlock,
  GripVertical
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { PropertyDefinition } from '../../../element-definitions/element-definition.types';
import type { TemplateElement } from '../../../types/template.types';

export interface PropertyRenderContext {
  prop: PropertyDefinition;
  value: unknown;
  updateProperty: (key: string, value: unknown) => void;
  updateProperties?: (updates: Record<string, unknown>) => void;
  element?: TemplateElement;
  maxHeight?: number;
}

export interface PropertyRenderer {
  render(context: PropertyRenderContext): React.JSX.Element | null;
}

// Text Property Renderer
class TextPropertyRenderer implements PropertyRenderer {
  render({ prop, value, updateProperty }: PropertyRenderContext): React.JSX.Element {
    return (
      <div key={prop.key} className="space-y-2">
        <Label htmlFor={prop.key} className="text-xs">{prop.label}</Label>
        <Input
          id={prop.key}
          type="text"
          value={(value as string) || ''}
          onChange={(e) => updateProperty(prop.key, e.target.value || undefined)}
          placeholder={prop.placeholder}
          className="h-8 text-xs"
        />
      </div>
    );
  }
}

// Textarea Property Renderer
class TextareaPropertyRenderer implements PropertyRenderer {
  render({ prop, value, updateProperty }: PropertyRenderContext): React.JSX.Element {
    return (
      <div key={prop.key} className="space-y-2">
        <Label htmlFor={prop.key} className="text-xs">{prop.label}</Label>
        <Textarea
          id={prop.key}
          value={(value as string) || ''}
          onChange={(e) => updateProperty(prop.key, e.target.value)}
          placeholder={prop.placeholder}
          className="min-h-[100px] text-xs"
        />
      </div>
    );
  }
}

// Textarea with Placeholders Property Renderer
class TextareaWithPlaceholdersPropertyRenderer implements PropertyRenderer {
  render({ prop, value, updateProperty }: PropertyRenderContext): React.JSX.Element {
    return <TextareaWithPlaceholders prop={prop} value={value} updateProperty={updateProperty} />;
  }
}

// Functional component for textarea with placeholders (needs hooks)
function TextareaWithPlaceholders({ 
  prop, 
  value, 
  updateProperty 
}: { 
  prop: PropertyDefinition; 
  value: unknown; 
  updateProperty: (key: string, value: unknown) => void;
}) {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  
  const insertPlaceholder = (placeholder: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentContent = (value as string) || '';
    const newContent = currentContent.slice(0, start) + placeholder + currentContent.slice(end);
    
    updateProperty(prop.key, newContent);
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + placeholder.length, start + placeholder.length);
    }, 0);
  };

  const PLACEHOLDERS = [
    '{Rechnungsnummer}', '{Rechnungsdatum}', '{Zahlungsziel}', 
    '{Bestellnummer}', '{Leitweg-ID}', '{Kundennummer}', '{Gesamtbetrag}',
    '{Firmenname}', '{Strasse}', '{Hausnummer}', '{PLZ}', '{Ort}', '{Telefon}',
    '{Ansprechpartner-Vorname}', '{Ansprechpartner-Nachname}', '{Ansprechpartner-EMail}',
    '{IBAN}', '{SWIFT/BIC}', '{Bank}', 
    '{E-Mail}', '{Website}',
    '{Amtsgericht}', '{HR-Nr}', '{Land}', '{Steuernummer}', '{Ust-IdNr}'
  ];

  return (
    <div key={prop.key} className="space-y-2">
      <Label htmlFor={prop.key} className="text-xs">{prop.label}</Label>
      <Textarea
        ref={textareaRef}
        id={prop.key}
        value={(value as string) || ''}
        onChange={(e) => updateProperty(prop.key, e.target.value)}
        placeholder={prop.placeholder}
        className="min-h-[100px] text-xs font-mono"
      />
      <div>
        <Label className="text-xs mb-1 block">Platzhalter einfügen</Label>
        <div className="flex flex-wrap gap-1">
          {PLACEHOLDERS.map((placeholder) => (
            <Badge
              key={placeholder}
              variant="outline"
              className="cursor-pointer hover:bg-primary hover:text-primary-foreground text-xs px-2 py-0.5"
              onClick={() => insertPlaceholder(placeholder)}
            >
              {placeholder}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}

// Number Property Renderer
class NumberPropertyRenderer implements PropertyRenderer {
  render({ prop, value, updateProperty, maxHeight, element }: PropertyRenderContext): React.JSX.Element {
    // Check if this is height/width property for a container with stretch
    const containerElement = element?.type === 'container' ? element as TemplateElement & { verticalAlign?: string; horizontalAlign?: string } : undefined;
    const isHeightDisabled = prop.key === 'height' && 
                             containerElement && 
                             containerElement.verticalAlign === 'stretch';
    const isWidthDisabled = prop.key === 'width' && 
                            containerElement && 
                            containerElement.horizontalAlign === 'stretch';
    
    return (
      <div key={prop.key} className="space-y-2">
        <Label htmlFor={prop.key} className="text-xs">{prop.label}</Label>
        <Input
          id={prop.key}
          type="number"
          value={(value as number) ?? ''}
          onChange={(e) => {
            const numValue = e.target.value ? Number(e.target.value) : undefined;
            // Apply maxHeight constraint for height property if specified
            if (prop.key === 'height' && maxHeight !== undefined && numValue !== undefined) {
              updateProperty(prop.key, Math.min(numValue, maxHeight));
            } else {
              updateProperty(prop.key, numValue);
            }
          }}
          min={prop.min}
          max={prop.key === 'height' && maxHeight !== undefined ? maxHeight : prop.max}
          placeholder={isHeightDisabled || isWidthDisabled ? 'Auto' : prop.placeholder}
          disabled={isHeightDisabled || isWidthDisabled}
          className="h-8 text-xs"
        />
      </div>
    );
  }
}

// Color Property Renderer
class ColorPropertyRenderer implements PropertyRenderer {
  render({ prop, value, updateProperty }: PropertyRenderContext): React.JSX.Element {
    return (
      <div key={prop.key} className="space-y-2">
        <Label htmlFor={prop.key} className="text-xs">{prop.label}</Label>
        <div className="flex gap-2">
          <Input
            id={`${prop.key}-picker`}
            type="color"
            value={(value as string) || prop.defaultValue || '#000000'}
            onChange={(e) => updateProperty(prop.key, e.target.value)}
            className="w-16 h-9 p-1"
          />
          <Input
            type="text"
            value={(value as string) || ''}
            onChange={(e) => updateProperty(prop.key, e.target.value || undefined)}
            placeholder={prop.placeholder}
            className="flex-1 h-9 text-xs"
          />
        </div>
      </div>
    );
  }
}

// Boolean Property Renderer
class BooleanPropertyRenderer implements PropertyRenderer {
  render({ prop, value, updateProperty }: PropertyRenderContext): React.JSX.Element {
    return (
      <div key={prop.key} className="flex items-center justify-between">
        <Label htmlFor={prop.key} className="text-xs">{prop.label}</Label>
        <Switch
          id={prop.key}
          checked={(value as boolean) ?? prop.defaultValue}
          onCheckedChange={(checked) => updateProperty(prop.key, checked)}
        />
      </div>
    );
  }
}

// Select Property Renderer
class SelectPropertyRenderer implements PropertyRenderer {
  render({ prop, value, updateProperty }: PropertyRenderContext): React.JSX.Element {
    return (
      <div key={prop.key} className="space-y-2">
        <Label htmlFor={prop.key} className="text-xs">{prop.label}</Label>
        <Select
          value={(value as string) || String(prop.defaultValue)}
          onValueChange={(val) => updateProperty(prop.key, val)}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {prop.options?.map(option => (
              <SelectItem key={String(option.value)} value={String(option.value)}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
}

// Text Align Property Renderer
class TextAlignPropertyRenderer implements PropertyRenderer {
  render({ prop, value, updateProperty }: PropertyRenderContext): React.JSX.Element {
    return (
      <div key={prop.key} className="space-y-2">
        <Label className="text-xs">{prop.label}</Label>
        <div className="grid grid-cols-4 gap-1">
          <Button
            type="button"
            variant={(value as string) === 'left' ? 'default' : 'outline'}
            size="sm"
            onClick={() => updateProperty(prop.key, 'left')}
            className="h-9"
          >
            <AlignLeft className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant={(value as string) === 'center' ? 'default' : 'outline'}
            size="sm"
            onClick={() => updateProperty(prop.key, 'center')}
            className="h-9"
          >
            <AlignCenter className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant={(value as string) === 'right' ? 'default' : 'outline'}
            size="sm"
            onClick={() => updateProperty(prop.key, 'right')}
            className="h-9"
          >
            <AlignRight className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant={(value as string) === 'justify' ? 'default' : 'outline'}
            size="sm"
            onClick={() => updateProperty(prop.key, 'justify')}
            className="h-9"
          >
            <AlignJustify className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }
}

// Horizontal Alignment Property Renderer
class AlignmentHorizontalPropertyRenderer implements PropertyRenderer {
  render({ prop, value, updateProperty, updateProperties, element }: PropertyRenderContext): React.JSX.Element {
    return (
      <div key={prop.key} className="space-y-2">
        <Label className="text-xs">{prop.label}</Label>
        <div className="grid grid-cols-4 gap-1">
          {[
            { value: 'left', label: 'Links', icon: '←' },
            { value: 'center', label: 'Mitte', icon: '↔' },
            { value: 'right', label: 'Rechts', icon: '→' },
            { value: 'stretch', label: 'Gestreckt', icon: '↔↔' },
          ].map(({ value: val, label, icon }) => (
            <Button
              key={val}
              type="button"
              variant={(value as string) === val ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                if (element && updateProperties) {
                  updateProperties({
                    ...element,
                    horizontalAlign: val as 'left' | 'center' | 'right' | 'stretch',
                    // Clear width when horizontal stretch is selected
                    width: val === 'stretch' ? undefined : element.width
                  });
                } else {
                  updateProperty(prop.key, val);
                }
              }}
              title={label}
              className="h-9"
            >
              {icon}
            </Button>
          ))}
        </div>
      </div>
    );
  }
}

// Vertical Alignment Property Renderer
class AlignmentVerticalPropertyRenderer implements PropertyRenderer {
  // Helper function to calculate container height based on content
  private calculateContainerHeight(container: TemplateElement): number {
    const containerAny = container as unknown as Record<string, unknown>;
    const rows = containerAny.rows as number || 1;
    const gap = containerAny.gap as number ?? 8;
    const paddingTop = containerAny.paddingTop as number ?? 0;
    const paddingBottom = containerAny.paddingBottom as number ?? 0;
    
    let totalContentHeight = 0;
    
    // If rowHeights are defined, use them
    const rowHeights = containerAny.rowHeights as (string | number)[] | undefined;
    if (rowHeights && rowHeights.length === rows) {
      for (let row = 0; row < rows; row++) {
        const rowHeight = rowHeights[row];
        const hStr = typeof rowHeight === 'string' ? rowHeight.trim() : String(rowHeight);
        
        if (hStr === 'auto') {
          // For auto rows, estimate minimum height (we can't calculate exact content here)
          totalContentHeight += 30; // Minimum estimated height per auto row
        } else if (hStr.endsWith('px')) {
          const px = parseFloat(hStr);
          totalContentHeight += isNaN(px) ? 30 : px;
        } else if (hStr.endsWith('fr')) {
          // For fr rows, give them reasonable space
          totalContentHeight += 50; // Minimum space per fr row
        } else {
          totalContentHeight += 30; // Default
        }
      }
    } else {
      // No rowHeights defined, estimate based on row count
      totalContentHeight = rows * 40; // 40px per row as default
    }
    
    const totalGapHeight = (rows - 1) * gap;
    return totalContentHeight + totalGapHeight + paddingTop + paddingBottom;
  }

  render({ prop, value, updateProperty, updateProperties, element }: PropertyRenderContext): React.JSX.Element {
    return (
      <div key={prop.key} className="space-y-2">
        <Label className="text-xs">{prop.label}</Label>
        <div className="grid grid-cols-4 gap-1">
          {[
            { value: 'top', label: 'Oben', icon: '↑' },
            { value: 'middle', label: 'Mitte', icon: '↕' },
            { value: 'bottom', label: 'Unten', icon: '↓' },
            { value: 'stretch', label: 'Gestreckt', icon: '↕↕' },
          ].map(({ value: val, label, icon }) => (
            <Button
              key={val}
              type="button"
              variant={(value as string) === val ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                if (element?.type === 'container' && updateProperties) {
                  if (val === 'stretch') {
                    // Switching to stretch: clear the height
                    updateProperties({ [prop.key]: val, height: undefined });
                  } else if (value === 'stretch' && val !== 'stretch') {
                    // Switching from stretch to top/middle/bottom: calculate and set height
                    const calculatedHeight = this.calculateContainerHeight(element);
                    updateProperties({ [prop.key]: val, height: calculatedHeight });
                  } else {
                    // Normal case: just update the alignment
                    updateProperty(prop.key, val);
                  }
                } else {
                  updateProperty(prop.key, val);
                }
              }}
              title={label}
              className="h-9"
            >
              {icon}
            </Button>
          ))}
        </div>
      </div>
    );
  }
}

// Font Weight Property Renderer
class FontWeightPropertyRenderer implements PropertyRenderer {
  render({ prop, value, updateProperty }: PropertyRenderContext): React.JSX.Element {
    return (
      <div key={prop.key} className="space-y-2">
        <Label className="text-xs">{prop.label}</Label>
        <Button
          type="button"
          variant={(value as string) === 'bold' ? 'default' : 'outline'}
          size="sm"
          onClick={() => updateProperty(prop.key, (value as string) === 'bold' ? 'normal' : 'bold')}
          className="h-8 w-8"
        >
          <Bold className="h-4 w-4" />
        </Button>
      </div>
    );
  }
}

// Font Style Property Renderer
class FontStylePropertyRenderer implements PropertyRenderer {
  render({ prop, value, updateProperty }: PropertyRenderContext): React.JSX.Element {
    return (
      <div key={prop.key} className="space-y-2">
        <Label className="text-xs">{prop.label}</Label>
        <Button
          type="button"
          variant={(value as string) === 'italic' ? 'default' : 'outline'}
          size="sm"
          onClick={() => updateProperty(prop.key, (value as string) === 'italic' ? 'normal' : 'italic')}
          className="h-8 w-8"
        >
          <Italic className="h-4 w-4" />
        </Button>
      </div>
    );
  }
}

// Text Decoration Property Renderer
class TextDecorationPropertyRenderer implements PropertyRenderer {
  render({ prop, value, updateProperty }: PropertyRenderContext): React.JSX.Element {
    return (
      <div key={prop.key} className="space-y-2">
        <Label className="text-xs">{prop.label}</Label>
        <div className="flex gap-1">
          <Button
            type="button"
            variant={(value as string) === 'underline' ? 'default' : 'outline'}
            size="sm"
            onClick={() => updateProperty(prop.key, (value as string) === 'underline' ? 'none' : 'underline')}
            className="h-8 w-8"
          >
            <Underline className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant={(value as string) === 'line-through' ? 'default' : 'outline'}
            size="sm"
            onClick={() => updateProperty(prop.key, (value as string) === 'line-through' ? 'none' : 'line-through')}
            className="h-8 w-8"
          >
            <Strikethrough className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }
}

// Image Upload Property Renderer
class ImageUploadPropertyRenderer implements PropertyRenderer {
  render({ prop, value, updateProperty, updateProperties }: PropertyRenderContext): React.JSX.Element {
    // Use a simple boolean flag instead of useState since we can't use hooks in class methods
    let isDragging = false;
    
    const processImageFile = (file: File) => {
      if (!file.type.startsWith('image/')) return;
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          // Store original dimensions and set element size to original image size
          if (updateProperties) {
            updateProperties({
              [prop.key]: reader.result as string,
              width: img.width,
              height: img.height,
              originalWidth: img.width,
              originalHeight: img.height,
              aspectRatioLocked: true
            });
          } else {
            updateProperty(prop.key, reader.result as string);
            updateProperty('width', img.width);
            updateProperty('height', img.height);
            updateProperty('originalWidth', img.width);
            updateProperty('originalHeight', img.height);
            updateProperty('aspectRatioLocked', true);
          }
        };
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    };
    
    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        processImageFile(file);
      }
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      isDragging = true;
    };

    const handleDragLeave = () => {
      isDragging = false;
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      isDragging = false;
      
      const file = e.dataTransfer.files[0];
      if (file) {
        processImageFile(file);
      }
    };

    const removeImage = () => {
      updateProperty(prop.key, undefined);
    };

    return (
      <div key={prop.key} className="space-y-2">
        <Label className="text-xs">{prop.label}</Label>
        
        {value ? (
          <div className="relative">
            <img 
              src={value as string} 
              alt="Preview" 
              className="w-full h-auto rounded border"
            />
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={removeImage}
              className="absolute top-2 right-2"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragging ? 'border-primary bg-accent' : 'border-muted-foreground hover:border-primary'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById(`file-input-${prop.key}`)?.click()}
          >
            <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm text-gray-600">
              Bild hochladen oder hierher ziehen
            </p>
            <input
              id={`file-input-${prop.key}`}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>
        )}
      </div>
    );
  }
}

// Image Dimensions Property Renderer (with aspect ratio lock)
class ImageDimensionsPropertyRenderer implements PropertyRenderer {
  render({ updateProperty, updateProperties, element }: PropertyRenderContext): React.JSX.Element {
    const imageElement = element as TemplateElement & { 
      aspectRatioLocked?: boolean;
      width?: number;
      height?: number;
      originalWidth?: number;
      originalHeight?: number;
    };
    
    const aspectRatioLocked = imageElement?.aspectRatioLocked ?? true;
    const width = imageElement?.width || 200;
    const height = imageElement?.height || 100;
    const originalWidth = imageElement?.originalWidth;
    const originalHeight = imageElement?.originalHeight;
    const hasOriginalDimensions = originalWidth && originalHeight;
    // Use original aspect ratio if available, otherwise current dimensions
    const aspectRatio = hasOriginalDimensions 
      ? originalWidth / originalHeight 
      : width / height;

    const handleWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newWidth = parseInt(e.target.value) || 0;
      if (aspectRatioLocked) {
        const newHeight = Math.round(newWidth / aspectRatio);
        if (updateProperties) {
          updateProperties({ width: newWidth, height: newHeight });
        } else {
          updateProperty('width', newWidth);
          updateProperty('height', newHeight);
        }
      } else {
        updateProperty('width', newWidth);
      }
    };

    const handleHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newHeight = parseInt(e.target.value) || 0;
      if (aspectRatioLocked) {
        const newWidth = Math.round(newHeight * aspectRatio);
        if (updateProperties) {
          updateProperties({ width: newWidth, height: newHeight });
        } else {
          updateProperty('width', newWidth);
          updateProperty('height', newHeight);
        }
      } else {
        updateProperty('height', newHeight);
      }
    };

    const toggleAspectRatioLock = () => {
      const newLocked = !aspectRatioLocked;
      updateProperty('aspectRatioLocked', newLocked);
    };

    const restoreOriginalDimensions = () => {
      if (originalWidth && originalHeight) {
        if (updateProperties) {
          updateProperties({ width: originalWidth, height: originalHeight });
        } else {
          updateProperty('width', originalWidth);
          updateProperty('height', originalHeight);
        }
      }
    };

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Bildgröße</Label>
          {hasOriginalDimensions && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={restoreOriginalDimensions}
              className="h-6 text-xs px-2"
              title="Original-Dimensionen wiederherstellen"
            >
              Zurücksetzen
            </Button>
          )}
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label htmlFor="image-width" className="text-xs">Breite (px)</Label>
            <Input
              id="image-width"
              type="number"
              value={width}
              onChange={handleWidthChange}
              min={10}
              max={2000}
            />
          </div>
          
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={toggleAspectRatioLock}
            className="h-9 w-9 p-0"
            title={aspectRatioLocked ? 'Seitenverhältnis entsperren' : 'Seitenverhältnis sperren'}
          >
            {aspectRatioLocked ? (
              <Lock className="h-4 w-4" />
            ) : (
              <Unlock className="h-4 w-4" />
            )}
          </Button>
          
          <div className="flex-1">
            <Label htmlFor="image-height" className="text-xs">Höhe (px)</Label>
            <Input
              id="image-height"
              type="number"
              value={height}
              onChange={handleHeightChange}
              min={10}
              max={2000}
            />
          </div>
        </div>
      </div>
    );
  }
}

// Table Columns Property Renderer
class TableColumnsPropertyRenderer implements PropertyRenderer {
  render({ prop, value, updateProperty }: PropertyRenderContext): React.JSX.Element {
    return <TableColumnsSelector prop={prop} value={value} updateProperty={updateProperty} />;
  }
}

// Sortable Column Item Component
function SortableColumnItem({ 
  id, 
  label, 
  checked, 
  onCheckedChange 
}: { 
  id: string; 
  label: string; 
  checked: boolean; 
  onCheckedChange: (checked: boolean) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-2 bg-muted/50 rounded-md"
    >
      <div {...attributes} {...listeners} className="cursor-move">
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>
      <Checkbox
        id={`col-${id}`}
        checked={checked}
        onCheckedChange={onCheckedChange}
      />
      <Label htmlFor={`col-${id}`} className="text-xs cursor-pointer flex-1">
        {label}
      </Label>
    </div>
  );
}

// Table Columns Selector Component
function TableColumnsSelector({ 
  prop, 
  value, 
  updateProperty 
}: { 
  prop: PropertyDefinition; 
  value: unknown; 
  updateProperty: (key: string, value: unknown) => void;
}) {
  const { t } = useTranslation();
  
  const defaultColumnOrder = ['position', 'description', 'productType', 'itemDate', 'quantity', 'unit', 'unitPrice', 'total'];
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const currentColumns = (value as string[]) || prop.defaultValue || defaultColumnOrder;
  
  const columnLabels: Record<string, string> = {
    position: t('invoiceTemplate.elementProperties.colPosition'),
    description: t('invoiceTemplate.elementProperties.colDescription'),
    productType: t('invoiceTemplate.elementProperties.colProductType'),
    itemDate: t('invoiceTemplate.elementProperties.colItemDate'),
    quantity: t('invoiceTemplate.elementProperties.colQuantity'),
    unit: t('invoiceTemplate.elementProperties.colUnit'),
    unitPrice: t('invoiceTemplate.elementProperties.colUnitPrice'),
    total: t('invoiceTemplate.elementProperties.colTotal'),
  };
  
  const allColumns = defaultColumnOrder.map(col => ({
    id: col,
    label: columnLabels[col] || col,
    checked: currentColumns.includes(col),
  }));
  
  const sortedColumns = [
    ...currentColumns.map((col: string) => allColumns.find(c => c.id === col)!).filter(Boolean),
    ...allColumns.filter(col => !currentColumns.includes(col.id))
  ];

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sortedColumns.findIndex(col => col.id === active.id);
      const newIndex = sortedColumns.findIndex(col => col.id === over.id);
      
      const newOrder = arrayMove(sortedColumns, oldIndex, newIndex);
      const newColumns = newOrder.filter(col => col.checked).map(col => col.id);
      updateProperty(prop.key, newColumns);
    }
  };

  const handleCheckedChange = (columnId: string, checked: boolean) => {
    if (checked) {
      const newColumns = [...currentColumns];
      const defaultIndex = defaultColumnOrder.indexOf(columnId);
      let insertIndex = newColumns.length;
      for (let i = 0; i < newColumns.length; i++) {
        const currentCol = newColumns[i];
        if (currentCol && defaultColumnOrder.indexOf(currentCol) > defaultIndex) {
          insertIndex = i;
          break;
        }
      }
      newColumns.splice(insertIndex, 0, columnId);
      updateProperty(prop.key, newColumns);
    } else {
      const newColumns = currentColumns.filter((c: string) => c !== columnId);
      updateProperty(prop.key, newColumns);
    }
  };

  return (
    <div key={prop.key} className="space-y-2">
      <Label className="text-xs">{prop.label}</Label>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sortedColumns.map(col => col.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {sortedColumns.map((col) => (
              <SortableColumnItem
                key={col.id}
                id={col.id}
                label={col.label}
                checked={col.checked}
                onCheckedChange={(checked) => handleCheckedChange(col.id, checked)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

// Invoice Info Rows Property Renderer
class InvoiceInfoRowsPropertyRenderer implements PropertyRenderer {
  render({ value, updateProperty }: PropertyRenderContext): React.JSX.Element {
    return <InvoiceInfoRowsSelector value={value} updateProperty={updateProperty} />;
  }
}

function InvoiceInfoRowsSelector({ value, updateProperty }: { value: unknown, updateProperty: (key: string, value: unknown) => void }) {
    const { t } = useTranslation();
    const rows = (value as Record<string, boolean>) || {};
    
    const rowDefinitions = [
      { key: 'invoiceNumber', labelKey: 'invoiceTemplate.elementProperties.invoiceInfoBlock.rows.invoiceNumber' },
      { key: 'invoiceDate', labelKey: 'invoiceTemplate.elementProperties.invoiceInfoBlock.rows.invoiceDate' },
      { key: 'paymentDueDate', labelKey: 'invoiceTemplate.elementProperties.invoiceInfoBlock.rows.paymentDueDate' },
      { key: 'vatId', labelKey: 'invoiceTemplate.elementProperties.invoiceInfoBlock.rows.vatId' },
      { key: 'customerNumber', labelKey: 'invoiceTemplate.elementProperties.invoiceInfoBlock.rows.customerNumber' },
      { key: 'pageNumber', labelKey: 'invoiceTemplate.elementProperties.invoiceInfoBlock.rows.pageNumber' },
    ];

    const handleToggle = (rowKey: string, checked: boolean) => {
      updateProperty('rows', {
        ...rows,
        [rowKey]: checked,
      });
    };

    return (
      <div className="space-y-3">
        <Label className="text-xs">{t('invoiceTemplate.elementProperties.invoiceInfoBlock.rowsConfig')}</Label>
        <div className="space-y-2">
          {rowDefinitions.map((row) => (
            <div key={row.key} className="flex items-center space-x-2">
              <Checkbox
                id={`row-${row.key}`}
                checked={rows[row.key] !== false}
                onCheckedChange={(checked) => handleToggle(row.key, checked as boolean)}
              />
              <label
                htmlFor={`row-${row.key}`}
                className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {t(row.labelKey)}
              </label>
            </div>
          ))}
        </div>
      </div>
    );
}

// Invoice Info Labels Property Renderer
class InvoiceInfoLabelsPropertyRenderer implements PropertyRenderer {
  render({ value, element, updateProperty }: PropertyRenderContext): React.JSX.Element {
    return <InvoiceInfoLabelsEditor value={value} element={element} updateProperty={updateProperty} />;
  }
}

function InvoiceInfoLabelsEditor({ value, element, updateProperty }: { value: unknown, element: any, updateProperty: (key: string, value: unknown) => void }) {
    const { t } = useTranslation();
    const labels = (value as Record<string, string>) || {};
    const rows = (element as any)?.rows || {};
    
    const rowDefinitions = [
      { key: 'invoiceNumber', labelKey: 'invoiceTemplate.elementProperties.invoiceInfoBlock.rows.invoiceNumber' },
      { key: 'invoiceDate', labelKey: 'invoiceTemplate.elementProperties.invoiceInfoBlock.rows.invoiceDate' },
      { key: 'paymentDueDate', labelKey: 'invoiceTemplate.elementProperties.invoiceInfoBlock.rows.paymentDueDate' },
      { key: 'vatId', labelKey: 'invoiceTemplate.elementProperties.invoiceInfoBlock.rows.vatId' },
      { key: 'customerNumber', labelKey: 'invoiceTemplate.elementProperties.invoiceInfoBlock.rows.customerNumber' },
      { key: 'pageNumber', labelKey: 'invoiceTemplate.elementProperties.invoiceInfoBlock.rows.pageNumber' },
    ];

    const visibleRows = rowDefinitions.filter(row => rows[row.key] !== false);

    if (visibleRows.length === 0) {
      return (
        <div className="space-y-2">
          <Label className="text-xs">{t('invoiceTemplate.elementProperties.invoiceInfoBlock.labelConfig')}</Label>
          <p className="text-xs text-muted-foreground">
            {t('invoiceTemplate.elementProperties.invoiceInfoBlock.noVisibleRows')}
          </p>
        </div>
      );
    }

    const handleLabelChange = (rowKey: string, newLabel: string) => {
      updateProperty('labels', {
        ...labels,
        [rowKey]: newLabel,
      });
    };

    return (
      <div className="space-y-3">
        <Label className="text-xs">{t('invoiceTemplate.elementProperties.invoiceInfoBlock.labelConfig')}</Label>
        <div className="space-y-2">
          {visibleRows.map((row) => (
            <div key={row.key} className="space-y-1">
              <Label htmlFor={`label-${row.key}`} className="text-xs text-muted-foreground">
                {t(row.labelKey)}
              </Label>
              <Input
                id={`label-${row.key}`}
                value={labels[row.key] || t(row.labelKey)}
                onChange={(e) => handleLabelChange(row.key, e.target.value)}
                className="h-8 text-xs"
              />
            </div>
          ))}
        </div>
      </div>
    );
}

// Property Renderer Factory
class PropertyRendererFactory {
  private renderers: Map<string, PropertyRenderer> = new Map();

  constructor() {
    // Register all renderers
    this.register('text', new TextPropertyRenderer());
    this.register('textarea', new TextareaPropertyRenderer());
    this.register('textarea-with-placeholders', new TextareaWithPlaceholdersPropertyRenderer());
    this.register('number', new NumberPropertyRenderer());
    this.register('color', new ColorPropertyRenderer());
    this.register('boolean', new BooleanPropertyRenderer());
    this.register('select', new SelectPropertyRenderer());
    this.register('text-align', new TextAlignPropertyRenderer());
    this.register('alignment-horizontal', new AlignmentHorizontalPropertyRenderer());
    this.register('alignment-vertical', new AlignmentVerticalPropertyRenderer());
    this.register('font-weight', new FontWeightPropertyRenderer());
    this.register('font-style', new FontStylePropertyRenderer());
    this.register('text-decoration', new TextDecorationPropertyRenderer());
    this.register('image-upload', new ImageUploadPropertyRenderer());
    this.register('image-dimensions', new ImageDimensionsPropertyRenderer());
    this.register('table-columns', new TableColumnsPropertyRenderer());
    this.register('invoice-info-rows', new InvoiceInfoRowsPropertyRenderer());
    this.register('invoice-info-labels', new InvoiceInfoLabelsPropertyRenderer());
  }

  register(type: string, renderer: PropertyRenderer): void {
    this.renderers.set(type, renderer);
  }

  getRenderer(type: string): PropertyRenderer | undefined {
    return this.renderers.get(type);
  }

  hasRenderer(type: string): boolean {
    return this.renderers.has(type);
  }
}

// Export singleton instance
export const propertyRendererFactory = new PropertyRendererFactory();

